import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { FileText, CheckCircle, XCircle, Download, Clock, User, File, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface QueueItem {
  queue_id: number;
  file_id: number;
  status: 'submitted';
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  notes: string | null;
  sophomorix_unid: string;
  original_name: string;
  file_type: 'official';
  description: string | null;
  category: string | null;
  created_at: string;
  student_name: string;
  student_first_name: string;
  student_class: string;
  submitted_by_username: string;
  submitted_by_first_name: string | null;
  submitted_by_last_name: string | null;
}

export default function StudentFilesQueue() {
  const [selectedQueueId, setSelectedQueueId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [actionType, setActionType] = useState<'accept' | 'reject' | null>(null);
  const queryClient = useQueryClient();

  // Fetch pending queue items
  const { data: queueItems = [], isLoading, error, refetch } = useQuery<QueueItem[]>(
    ['student-files-queue'],
    async () => {
      const response = await api.get('/student-files/queue/pending');
      return response.data.data;
    },
    {
      retry: 3,
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Accept file mutation
  const acceptMutation = useMutation(
    async ({ queueId, notes }: { queueId: number; notes?: string }) => {
      const response = await api.post(`/student-files/queue/${queueId}/accept`, { notes });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files-queue']);
        queryClient.invalidateQueries(['student-files']);
        setSelectedQueueId(null);
        setActionType(null);
        setNotes('');
        toast.success('Datei akzeptiert');
      },
      onError: (error: any) => {
        console.error('Error accepting file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Akzeptieren der Datei';
        toast.error(message);
      },
    }
  );

  // Reject file mutation
  const rejectMutation = useMutation(
    async ({ queueId, rejection_reason, notes }: { queueId: number; rejection_reason: string; notes?: string }) => {
      const response = await api.post(`/student-files/queue/${queueId}/reject`, { rejection_reason, notes });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-files-queue']);
        queryClient.invalidateQueries(['student-files']);
        setSelectedQueueId(null);
        setActionType(null);
        setRejectionReason('');
        setNotes('');
        toast.success('Datei abgelehnt');
      },
      onError: (error: any) => {
        console.error('Error rejecting file:', error);
        const message = error?.response?.data?.error || 'Fehler beim Ablehnen der Datei';
        toast.error(message);
      },
    }
  );

  const handleAccept = (queueId: number) => {
    setSelectedQueueId(queueId);
    setActionType('accept');
    setNotes('');
  };

  const handleReject = (queueId: number) => {
    setSelectedQueueId(queueId);
    setActionType('reject');
    setRejectionReason('');
    setNotes('');
  };

  const handleSubmitAction = () => {
    if (!selectedQueueId) return;

    if (actionType === 'accept') {
      acceptMutation.mutate({ queueId: selectedQueueId, notes: notes || undefined });
    } else if (actionType === 'reject') {
      if (!rejectionReason.trim()) {
        toast.error('Bitte geben Sie einen Ablehnungsgrund ein');
        return;
      }
      rejectMutation.mutate({
        queueId: selectedQueueId,
        rejection_reason: rejectionReason,
        notes: notes || undefined,
      });
    }
  };

  const handleCancelAction = () => {
    setSelectedQueueId(null);
    setActionType(null);
    setRejectionReason('');
    setNotes('');
  };

  const handleDownload = (item: QueueItem) => {
    window.open(`/api/student-files/${item.sophomorix_unid}/download/${item.file_id}`, '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schülerakte - Warteschlange</h1>
          <p className="mt-2 text-sm text-gray-600">
            Prüfen und genehmigen Sie eingereichte Dateien für die offizielle Schülerakte
          </p>
        </div>
        <div className="bg-white p-8 rounded-lg border border-gray-200">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Lade Warteschlange...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schülerakte - Warteschlange</h1>
          <p className="mt-2 text-sm text-gray-600">
            Prüfen und genehmigen Sie eingereichte Dateien für die offizielle Schülerakte
          </p>
        </div>
        <div className="bg-white p-8 rounded-lg border border-red-200">
          <div className="text-red-600 text-center">
            Fehler beim Laden der Warteschlange
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText className="h-8 w-8 mr-3 text-blue-600" />
          Schülerakte - Warteschlange
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Prüfen und genehmigen Sie eingereichte Dateien für die offizielle Schülerakte
        </p>
      </div>

      {/* Queue Items */}
      <div className="bg-white rounded-lg border border-gray-200">
        {queueItems.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 font-medium">Keine ausstehenden Dateien</p>
            <p className="text-sm text-gray-500 mt-2">
              Alle eingereichten Dateien wurden bearbeitet.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {queueItems.map((item) => (
              <div
                key={item.queue_id}
                className={`p-6 ${
                  selectedQueueId === item.queue_id ? 'bg-blue-50' : 'hover:bg-gray-50'
                } transition-colors`}
              >
                {selectedQueueId === item.queue_id && actionType ? (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      {actionType === 'accept' ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {actionType === 'accept' ? 'Datei akzeptieren' : 'Datei ablehnen'}
                      </h3>
                    </div>
                    {actionType === 'reject' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ablehnungsgrund <span className="text-red-600">*</span>
                        </label>
                        <textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Bitte geben Sie einen Grund für die Ablehnung ein..."
                          className="input w-full border-gray-300 focus:border-red-500 focus:ring-red-500"
                          rows={3}
                          required
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notizen (optional)
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Zusätzliche Notizen..."
                        className="input w-full border-gray-300"
                        rows={2}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleSubmitAction}
                        disabled={actionType === 'reject' && !rejectionReason.trim()}
                        className={`btn btn-sm flex items-center ${
                          actionType === 'accept' ? 'btn-primary' : 'btn-danger'
                        }`}
                      >
                        {actionType === 'accept' ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Akzeptieren
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-1" />
                            Ablehnen
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleCancelAction}
                        className="btn btn-secondary btn-sm"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <File className="h-5 w-5 text-gray-400" />
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {item.original_name}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-sm text-gray-600">
                                <User className="h-4 w-4 inline mr-1" />
                                {item.student_first_name} {item.student_name} ({item.student_class})
                              </span>
                            </div>
                          </div>
                        </div>
                        {item.category && (
                          <span className="inline-block text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2">
                            {item.category}
                          </span>
                        )}
                        {item.description && (
                          <p className="text-sm text-gray-600 mt-2">{item.description}</p>
                        )}
                        <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                          <span>
                            <Clock className="h-3 w-3 inline mr-1" />
                            Eingereicht: {formatDate(item.submitted_at)}
                          </span>
                          <span>
                            Von: {item.submitted_by_first_name || ''} {item.submitted_by_last_name || ''} ({item.submitted_by_username})
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleDownload(item)}
                          className="btn btn-secondary btn-sm flex items-center"
                          title="Herunterladen"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleAccept(item.queue_id)}
                          className="btn btn-primary btn-sm flex items-center"
                          title="Akzeptieren"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Akzeptieren
                        </button>
                        <button
                          onClick={() => handleReject(item.queue_id)}
                          className="btn btn-danger btn-sm flex items-center"
                          title="Ablehnen"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Ablehnen
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
